import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Users, Calendar, FileText } from 'lucide-react';
import { Label } from '@/components/ui/label';

export interface DuplicateCandidate {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  status: string | null;
  site_name?: string;
  room_number?: string;
  match_reasons: string[];
}

interface DuplicateCheckDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOverride: (reason: string) => void;
  onCancel: () => void;
  candidates: DuplicateCandidate[];
  newClientName: string;
}

export const DuplicateCheckDialog: React.FC<DuplicateCheckDialogProps> = ({
  isOpen,
  onClose,
  onOverride,
  onCancel,
  candidates,
  newClientName,
}) => {
  const [overrideReason, setOverrideReason] = useState('');

  const handleOverride = () => {
    if (!overrideReason.trim()) return;
    onOverride(overrideReason.trim());
    setOverrideReason('');
  };

  const handleCancel = () => {
    setOverrideReason('');
    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Potential Duplicate Detected
          </DialogTitle>
          <DialogDescription>
            We found {candidates.length} existing record{candidates.length > 1 ? 's' : ''} that may match "{newClientName}". Please review before proceeding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {candidates.map((candidate) => (
            <div
              key={candidate.id}
              className="border border-border rounded-lg p-4 bg-muted/30 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-semibold">
                    {candidate.first_name} {candidate.last_name}
                  </span>
                  {candidate.status && (
                    <Badge variant={candidate.status === 'checked-in' ? 'default' : 'secondary'} className="text-xs">
                      {candidate.status}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                {candidate.date_of_birth && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    DOB: {new Date(candidate.date_of_birth).toLocaleDateString()}
                  </div>
                )}
                {candidate.room_number && (
                  <div>Room: {candidate.room_number}</div>
                )}
                {candidate.site_name && (
                  <div>Site: {candidate.site_name}</div>
                )}
              </div>

              <div className="flex flex-wrap gap-1">
                {candidate.match_reasons.map((reason, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/30">
                    {reason}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-2">
          <Label htmlFor="override-reason" className="text-sm font-medium">
            To proceed anyway, provide a reason <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="override-reason"
            placeholder="e.g., Verified different person with same name, re-intake after discharge..."
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            rows={2}
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleCancel} className="flex-1">
            Cancel — Don't Create
          </Button>
          <Button
            onClick={handleOverride}
            disabled={!overrideReason.trim()}
            className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            Override & Create Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
